import { activePage, replaceActivePage } from "../native/pages";
import type { EngineDocument } from "../scene/model";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import { documentPoint, type Box, type Camera } from "../native/geometry";
import {
  cropBounds,
  cropDocument,
  cropExpansion,
  resizeCrop,
  type CropEdge,
} from "../native/crop";
import { pixelsPerUnit, displayMeasurement } from "../native/page";
import { rasterCrop } from "../render/native-crop-raster";
import type { ImageStore } from "../render/image-store";
import { gridPoint, type GridSettings } from "../native/grid";

export class NativeCrop {
  box: Box | null = null;
  selectionRegion?: () => Box | null;
  private temporarySelection = false;
  source: "base" | "tool" = "tool";
  busy = false;
  private generation = -1;
  private revision = -1;
  private token = 0;
  private gesture: { id: number; edge: CropEdge; before: Box } | null = null;
  private baseChanged: ((box: Box) => void) | null = null;
  private baseClosed: (() => void) | null = null;
  private panel = document.getElementById("crop-properties")!;
  private compact = document.getElementById("native-crop-compact")!;
  constructor(
    private overlay: HTMLCanvasElement,
    private session: NativeSession,
    private pointer: NativePointer,
    private camera: Camera,
    private images: ImageStore,
    private grid: GridSettings,
    private changed: () => void,
    private message: (text: string, error?: boolean) => void,
    private blocked: () => boolean,
    private area: () => Box,
  ) {
    document.getElementById("tool-crop")!.onclick = () => this.activate("tool");
    document.getElementById("crop-apply")!.onclick = () => void this.apply();
    for (const key of ["width", "height"] as const)
      for (const prefix of ["crop-", "native-crop-compact-"]) {
        const field = document.getElementById(prefix + key) as HTMLInputElement;
        field.oninput = () => {
          if (this.box && !this.busy) {
            this.box = {
              ...this.box,
              [key]: Number(field.value) * pixelsPerUnit(session.page.unit),
            };
            this.changed();
          }
        };
      }
    document.getElementById("crop-delete-clipped")!.onchange = () =>
      this.changed();
    document.getElementById("native-crop-compact-delete")!.onclick = () => {
      const field = this.deleteField;
      field.checked = !field.checked;
      this.changed();
    };
    document.getElementById("native-crop-compact-apply")!.onclick = () =>
      void this.apply();
    for (const key of ["width", "height"])
      document.getElementById(`native-crop-compact-${key}-open`)!.onclick =
        () => {
          const popup = document.getElementById(`native-crop-${key}-popup`)!;
          const hidden = popup.hidden;
          this.closePopups();
          popup.hidden = !hidden;
        };
    for (const type of [
      "pointerdown",
      "pointermove",
      "pointerup",
      "pointercancel",
    ] as const)
      overlay.addEventListener(type, (e) => this.event(e), true);
    overlay.addEventListener("lostpointercapture", () => this.cancelGesture());
    window.addEventListener("blur", () => this.cancelGesture());
    document.addEventListener(
      "click",
      (e) => {
        if (!this.box || this.blocked()) return;
        const target = e.target as HTMLElement;
        if (
          target.closest(
            "#native-select,#native-shape-group,#native-image,[data-layer-id]",
          )
        )
          this.close();
      },
      true,
    );
    window.addEventListener(
      "keydown",
      (e) => {
        if (!this.box || this.blocked()) return;
        if (e.key === "Escape") {
          if (
            this.source === "base" &&
            !this.gesture &&
            document.getElementById("native-page-apply")!.hidden === false
          )
            return;
          e.preventDefault();
          e.stopImmediatePropagation();
          const popup = this.compact.querySelector<HTMLElement>(
            "[data-crop-popup]:not([hidden])",
          );
          if (popup) popup.hidden = true;
          else if (this.gesture) this.cancelGesture();
          else this.close();
          this.changed();
          return;
        }
        if (
          (e.target as HTMLElement).closest(
            "input,textarea,select,[contenteditable=true]",
          )
        )
          return;
        if (this.busy || e.key === "Delete") {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true,
    );
  }
  private closePopups() {
    this.compact
      .querySelectorAll<HTMLElement>("[data-crop-popup]")
      .forEach((p) => (p.hidden = true));
  }
  bindBase(changed: (box: Box) => void, closed: () => void) {
    this.baseChanged = changed;
    this.baseClosed = closed;
  }
  get gesturing() {
    return !!this.gesture;
  }
  get deleteField() {
    return document.getElementById("crop-delete-clipped") as HTMLInputElement;
  }
  get dirty() {
    const b = this.box,
      p = this.session.page.size;
    return (
      !!b &&
      (b.x !== 0 || b.y !== 0 || b.width !== p.width || b.height !== p.height)
    );
  }
  get active() {
    return !!this.box;
  }
  activate(source: "base" | "tool") {
    if (this.blocked() || this.busy) return;
    this.close();
    this.pointer.setTool("select");
    this.session.select([]);
    this.source = source;
    this.reset();
    if (source === "tool" && this.selectionRegion?.())
      this.box = { ...this.selectionRegion()! };
    if (
      source === "tool" &&
      document
        .getElementById("native-toggle-right")!
        .getAttribute("aria-expanded") === "false"
    )
      document.getElementById("native-toggle-right")!.click();
    this.changed();
  }
  reset() {
    this.box = { x: 0, y: 0, ...this.session.page.size };
    this.generation = this.session.generation;
    this.revision = this.session.revision;
    this.changed();
  }
  close() {
    const wasBase = this.box && this.source === "base";
    this.token++;
    this.busy = false;
    this.cancelGesture();
    this.box = null;
    this.panel.hidden = true;
    this.compact.hidden = true;
    this.closePopups();
    if (wasBase) this.baseClosed?.();
    this.changed();
  }
  setSize(size: { width: number; height: number }) {
    if (this.box && this.source === "base") this.box = { ...this.box, ...size };
  }
  handle(e: PointerEvent): CropEdge | null {
    if (!this.box) return null;
    const b = this.box,
      z = this.camera.zoom;
    const points: [CropEdge, number, number][] = [
      ["left", b.x, b.y + b.height / 2],
      ["right", b.x + b.width, b.y + b.height / 2],
      ["top", b.x + b.width / 2, b.y],
      ["bottom", b.x + b.width / 2, b.y + b.height],
    ];
    return (
      points.find(
        ([, x, y]) =>
          Math.hypot(
            x * z + this.camera.x - e.clientX,
            y * z + this.camera.y - e.clientY,
          ) < (e.pointerType === "touch" ? 18 : 10),
      )?.[0] ?? null
    );
  }
  private event(e: PointerEvent) {
    if (!this.box || this.blocked()) return;
    if (e.type === "pointerdown" && (e.ctrlKey || e.metaKey) && !this.gesture)
      this.temporarySelection = true;
    if (this.temporarySelection) {
      if (e.type === "pointerup" || e.type === "pointercancel") {
        queueMicrotask(() => {
          this.temporarySelection = false;
          this.session.select([]);
          this.changed();
        });
      }
      return;
    }
    if (
      this.pointer.space ||
      e.button === 1 ||
      (!this.gesture && this.pointer.active)
    )
      return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.busy) return;
    if (e.type === "pointerdown") {
      if (e.button !== 0) return;
      this.overlay.focus();
      const edge = this.handle(e);
      if (edge) {
        this.gesture = { id: e.pointerId, edge, before: { ...this.box } };
        this.overlay.setPointerCapture(e.pointerId);
      }
    } else if (e.type === "pointermove") {
      if (this.gesture?.id === e.pointerId) {
        const point = gridPoint(
          documentPoint({ x: e.clientX, y: e.clientY }, this.camera),
          { x: this.session.page.size.width, y: this.session.page.size.height },
          this.camera.zoom,
          this.grid,
        );
        this.box = resizeCrop(
          this.gesture.before,
          this.gesture.edge,
          point,
          e.altKey,
        );
        this.baseSync();
      } else
        this.overlay.style.cursor = this.handle(e)
          ? ["left", "right"].includes(this.handle(e)!)
            ? "ew-resize"
            : "ns-resize"
          : "default";
    } else if (e.type === "pointerup" && this.gesture?.id === e.pointerId) {
      this.gesture = null;
      if (this.overlay.hasPointerCapture(e.pointerId))
        this.overlay.releasePointerCapture(e.pointerId);
      this.baseSync();
    } else if (e.type === "pointercancel") this.cancelGesture();
    this.changed();
  }
  private baseSync() {
    if (this.source === "base" && this.box) this.baseChanged?.(this.box);
  }
  private cancelGesture() {
    if (this.gesture) {
      const id = this.gesture.id;
      this.box = this.gesture.before;
      this.gesture = null;
      if (this.overlay.hasPointerCapture(id))
        this.overlay.releasePointerCapture(id);
      this.baseSync();
      this.changed();
    }
  }
  async prepare(settings: EngineDocument) {
    if (!this.box || this.busy || this.gesture)
      throw new Error("Завершите изменение рамки.");
    const box = cropBounds(this.box),
      original = this.session.document,
      rev = this.session.revision,
      gen = this.session.generation,
      token = ++this.token;
    const document = replaceActivePage(original, {
      ...activePage(original),
      unit: activePage(settings).unit,
      workspaceColor: activePage(settings).workspaceColor,
    });
    this.busy = true;
    this.changed();
    const current = () =>
      token === this.token &&
      rev === this.session.revision &&
      gen === this.session.generation &&
      (!this.blocked() ||
        ((
          globalThis.document.getElementById(
            "native-page-warning",
          ) as HTMLDialogElement
        ).open &&
          globalThis.document.querySelectorAll("dialog[open]").length === 1));
    try {
      const result = this.deleteField.checked
        ? await rasterCrop(document, box, this.images, current)
        : cropDocument(document, box);
      if (!current()) throw new Error("Кадрирование отменено.");
      return { document: result, box, previous: activePage(original).size };
    } finally {
      if (token === this.token) {
        this.busy = false;
        this.changed();
      }
    }
  }
  complete(result: { box: Box; previous: { width: number; height: number } }) {
    if (cropExpansion(result.box, result.previous)) {
      this.camera.x += result.box.x * this.camera.zoom;
      this.camera.y += result.box.y * this.camera.zoom;
    } else {
      const a = this.area();
      this.camera.x = a.x + (a.width - result.box.width * this.camera.zoom) / 2;
      this.camera.y =
        a.y + (a.height - result.box.height * this.camera.zoom) / 2;
    }
    if (this.source === "base") this.reset();
    else this.close();
    this.pointer.setTool("select");
    this.message(
      `Холст кадрирован до ${result.box.width} × ${result.box.height} px.`,
    );
    this.changed();
  }
  private async apply() {
    if (this.source !== "tool" || this.blocked()) return;
    try {
      const result = await this.prepare(this.session.document);
      this.session.document = result.document;
      this.session.finish();
      this.complete(result);
    } catch (error) {
      this.message((error as Error).message, true);
    }
  }
  render(ctx: CanvasRenderingContext2D) {
    if (
      this.box &&
      (this.generation !== this.session.generation ||
        this.revision !== this.session.revision)
    ) {
      if (this.generation !== this.session.generation) this.close();
      else this.reset();
    }
    if (this.session.selectedIds.length && this.box && !this.temporarySelection)
      this.close();
    this.panel.hidden = !this.box || this.source !== "tool";
    this.compact.hidden = this.panel.hidden;
    document
      .getElementById("tool-crop")!
      .setAttribute("aria-pressed", String(!!this.box));
    if (!this.box) return;
    document
      .getElementById("native-select")!
      .setAttribute("aria-pressed", "false");
    if (this.source === "tool") {
      document.getElementById("native-selection-title")!.textContent =
        "Кадрирование";
      document.getElementById("native-selection-count")!.textContent = "";
    }
    const box = this.box,
      unit = this.session.page.unit;
    for (const key of ["width", "height"] as const) {
      for (const prefix of ["crop-", "native-crop-compact-"]) {
        const field = document.getElementById(prefix + key) as HTMLInputElement;
        if (document.activeElement !== field)
          field.value = displayMeasurement(box[key], unit);
        field.step = unit === "px" ? "1" : "0.1";
        field.disabled = this.busy;
      }
      document.getElementById(`crop-${key}-unit`)!.textContent = unit;
    }
    for (const id of ["crop-apply", "native-crop-compact-apply"])
      (document.getElementById(id) as HTMLButtonElement).disabled =
        this.busy || this.gesturing;
    document
      .getElementById("native-crop-compact-delete")!
      .setAttribute("aria-pressed", String(this.deleteField.checked));
    if (
      ![box.x, box.y, box.width, box.height].every(Number.isFinite) ||
      box.width < 1 ||
      box.height < 1
    )
      return;
    const { x, y, zoom: z } = this.camera,
      p = this.session.page.size;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(z, z);
    ctx.beginPath();
    ctx.rect(0, 0, p.width, p.height);
    ctx.clip();
    ctx.fillStyle = "rgba(15,23,42,0.58)";
    const l = Math.max(0, Math.min(p.width, box.x)),
      t = Math.max(0, Math.min(p.height, box.y)),
      r = Math.max(0, Math.min(p.width, box.x + box.width)),
      b = Math.max(0, Math.min(p.height, box.y + box.height));
    ctx.fillRect(0, 0, p.width, t);
    ctx.fillRect(0, b, p.width, p.height - b);
    ctx.fillRect(0, t, l, b - t);
    ctx.fillRect(r, t, p.width - r, b - t);
    ctx.restore();
    ctx.save();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x + box.x * z, y + box.y * z, box.width * z, box.height * z);
    ctx.strokeStyle = "#2563eb";
    ctx.lineWidth = 1;
    ctx.strokeRect(
      x + box.x * z - 0.5,
      y + box.y * z - 0.5,
      box.width * z + 1,
      box.height * z + 1,
    );
    ctx.fillStyle = "#ffffff";
    for (const [px, py] of [
      [box.x, box.y + box.height / 2],
      [box.x + box.width, box.y + box.height / 2],
      [box.x + box.width / 2, box.y],
      [box.x + box.width / 2, box.y + box.height],
    ]) {
      ctx.fillRect(x + px! * z - 5, y + py! * z - 5, 10, 10);
      ctx.strokeRect(x + px! * z - 5, y + py! * z - 5, 10, 10);
    }
    ctx.restore();
  }
}
